// Orders tab — the full list: current (active, from turnaround) and past
// (completed history, from all-orders). Every row opens the order detail.
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useRouter } from "../lib/router";
import type { Dashboard, PastOrder } from "../lib/types";
import { formatDate, queueLabel, rollBreakdown } from "../lib/format";
import { Card, EmptyState, ScreenHeader, Spinner, Money } from "./ui";

function OrderRow({ onClick, icon, tone, title, subtitle, right }: { onClick: () => void; icon: string; tone: string; title: string; subtitle: string; right?: React.ReactNode }) {
  return (
    <button onClick={onClick} className="w-full">
      <Card className="p-4 flex items-center gap-3 active:bg-line-soft/50">
        <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${tone}`}>
          <i className={`ph-fill ${icon}`} />
        </div>
        <div className="flex-1 text-left min-w-0">
          <div className="text-ink font-medium truncate">{title}</div>
          <div className="text-[12px] text-ink-subtle truncate">{subtitle}</div>
        </div>
        {right}
        <i className="ph ph-caret-right text-ink-faint" />
      </Card>
    </button>
  );
}

export function Orders() {
  const { navigate } = useRouter();
  const dash = useQuery({ queryKey: ["dashboard"], queryFn: () => api.get<Dashboard>("/api/dashboard"), staleTime: 60_000 });
  const hist = useQuery({ queryKey: ["orders", "history"], queryFn: () => api.get<{ orders: PastOrder[] }>("/api/orders/history"), staleTime: 60_000 });

  const active = dash.data;
  const go = (id: string) => navigate(`/order/${encodeURIComponent(id)}`);
  const hasActive = !!active && (active.readyOrders.length || active.pendingOrders.length || active.unpaidInvoices.length);

  // Past = history minus whatever turnaround currently reports as active.
  const activeIds = new Set<string>([
    ...(active?.readyOrders.map((o) => o.invoiceId) || []),
    ...(active?.pendingOrders.map((o) => o.invoiceId) || []),
    ...(active?.unpaidInvoices.map((i) => i.id) || []),
  ]);
  const past = (hist.data?.orders || []).filter((o) => !o.invoiceId || !activeIds.has(o.invoiceId));

  return (
    <div className="pb-4">
      <ScreenHeader title="Orders" subtitle="Current and past" />

      {dash.isLoading && <Spinner label="Loading your orders…" />}

      {active && (
        <>
          {(active.readyOrders.length > 0 || active.pendingOrders.length > 0 || active.unpaidInvoices.length > 0) && (
            <section className="px-4 mb-5">
              <h2 className="text-[13px] font-semibold uppercase tracking-wide text-ink-subtle px-1 mb-2">Current</h2>
              <div className="flex flex-col gap-2.5">
                {active.readyOrders.map((o) => (
                  <OrderRow
                    key={o.invoiceId}
                    onClick={() => go(o.invoiceId)}
                    icon="ph-check-circle"
                    tone="bg-green-50 text-sev-success"
                    title={o.message || "Scans ready"}
                    subtitle={o.dropDate ? `Dropped ${o.dropDate}` : "Ready"}
                    right={o.amount ? <span className="text-orange text-[13px] font-medium">${o.amount}</span> : undefined}
                  />
                ))}
                {active.unpaidInvoices.map((inv) => (
                  <OrderRow
                    key={inv.id}
                    onClick={() => go(inv.id)}
                    icon="ph-credit-card"
                    tone="bg-orange-soft text-orange"
                    title={inv.description || "Invoice"}
                    subtitle="Awaiting payment"
                    right={<span className="text-orange text-[13px] font-medium">${inv.amount}</span>}
                  />
                ))}
                {active.pendingOrders.map((o) => {
                  const est = formatDate(o.turnaround?.estimatedFinalDate);
                  return (
                    <OrderRow
                      key={o.invoiceId}
                      onClick={() => go(o.invoiceId)}
                      icon="ph-flask"
                      tone="bg-navy-soft text-navy"
                      title={rollBreakdown(o).join(" · ") || "Film order"}
                      subtitle={est ? `${queueLabel(o.turnaround?.queueStatus)} · ready ${est}` : queueLabel(o.turnaround?.queueStatus)}
                    />
                  );
                })}
              </div>
            </section>
          )}
        </>
      )}

      {/* Past */}
      <section className="px-4">
        <h2 className="text-[13px] font-semibold uppercase tracking-wide text-ink-subtle px-1 mb-2">Past orders</h2>
        {hist.isLoading ? (
          <Spinner />
        ) : past.length ? (
          <div className="flex flex-col gap-2.5">
            {past.map((o, i) => (
              <OrderRow
                key={(o.invoiceId || "") + i}
                onClick={() => o.invoiceId && go(o.invoiceId)}
                icon="ph-check"
                tone="bg-line-soft text-ink-muted"
                title={rollBreakdown(o).join(" · ") || (o.totalRolls ? `${o.totalRolls} roll${o.totalRolls === 1 ? "" : "s"}` : "Order")}
                subtitle={[formatDate(o.date), o.returnStatus || undefined].filter(Boolean).join(" · ") || "Completed"}
              />
            ))}
          </div>
        ) : !hasActive ? (
          <EmptyState icon="ph-tray" title="No orders yet" hint="Your drop-offs and mail-ins will show up here." />
        ) : (
          <p className="text-sm text-ink-subtle px-1">No past orders yet.</p>
        )}
      </section>
    </div>
  );
}
